// The yardstick — what a motion NEEDS, in words.
//
// ONE component, used by BOTH screens: the ballot paper before the vote is cast,
// and the results sheet beside the tally afterwards. It was extracted from
// BallotPaper rather than copied into the results view, because a second copy is
// exactly how the two screens start telling a member two different things about
// the same clause.
//
// The wording stays PRESENT TENSE on the results sheet too ("What this motion
// needs", not "needed"). A past-tense table keyed on the same MajorityBase would
// be a second source of truth for the most misunderstood rule in the
// Constitution — under `summoned-per-house` a member who does not vote has cast
// a No — and the two would drift the first time either was edited. src/thresholds.ts
// is the only place those three sentences exist.

import { useId, type JSX } from 'react';
import type { MajorityBase } from '@cca/decides-count';
import { THRESHOLD_COPY } from './thresholds';

interface YardstickProps {
  base: MajorityBase;
  /**
   * The heading level to render, so neither screen skips one: `2` on the ballot
   * paper (where the motion title is the page's h1), `3` on the results sheet
   * (where each item is an h2).
   */
  level: 2 | 3;
  /**
   * An id for the SENTENCE, so a caller can point an `aria-describedby` at it.
   *
   * The ballot paper passes one and names it on the vote fieldset: a section
   * heading above a radio group is read by a screen reader only if the user
   * happens to travel through it, and arrowing or tabbing into the group goes
   * straight past — so the one sentence saying what majority the vote is
   * measured against would be met by sighted users and missed by exactly the
   * people who cannot glance up at it.
   *
   * OPTIONAL, and the results sheet passes nothing. There the sentence sits in
   * reading order beside the tally and describes no control, so an id would be
   * an attribute pointing at nobody; `undefined` renders no `id` at all, and
   * that sheet's markup is unchanged.
   */
  textId?: string;
}

export function Yardstick({ base, level, textId }: YardstickProps): JSX.Element {
  const titleId = useId();
  const copy = THRESHOLD_COPY[base];
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <section className="ballot-yardstick" aria-labelledby={titleId}>
      <Heading className="ballot-yardstick-title" id={titleId}>
        What this motion needs
      </Heading>
      <p className="ballot-yardstick-text" id={textId}>
        {copy.sentence}
        {copy.emphasis ? (
          <>
            {' '}
            <strong>{copy.emphasis}</strong>
          </>
        ) : null}
      </p>
    </section>
  );
}
